import React, { useEffect } from 'react';
import { AuthWrapper } from '../../components/AuthWrapper/AuthWrapper';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePreventBack } from 'src/hooks/usePreventBack';
import { clearOnLogout } from 'src/utils/clear.util';
import googleIcon from 'src/assets/img/google-icon.svg';
import { Button } from 'src/components/ui/button';
import Input from 'src/components/Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { emailScheme } from 'src/utils/validation-schemes';
import { useLoader } from 'src/provider/LoaderProvider';
import { useValidateRegistrationFieldsMutation } from 'src/services/usersSsoService';
import { PATH_AUTH_SPA, PATH_REGISTER } from 'src/constants/spa-routes';
import { setEmail } from 'src/store/slices/registerSlice';
import { getEnvironment } from 'src/env/env';
import { useAppDispatch } from 'src/store';

const Title = () => (
  <div>
    <p className={'uppercase text-[1.375rem] text-black font-bold'}>Welcome to CoSupport AI Hub!</p>
  </div>
);

const RegisterPage = () => {
  const loader = useLoader();
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email');
  const [validateFields, { isError: isErrorValidateFields, isSuccess: isSuccessValidateFields }] =
    useValidateRegistrationFieldsMutation();

  useEffect(() => {
    clearOnLogout(dispatch);
  }, []);

  useEffect(() => {
    if (isErrorValidateFields) {
      setError('email', { message: 'Email already exists' });
      setEmailErrorMessage('Email already exists');
      loader.hide();
    }
  }, [isErrorValidateFields]);

  useEffect(() => {
    if (isSuccessValidateFields) {
      dispatch(setEmail(email));
      loader.hide();

      if (globalThis.gtag) {
        globalThis.gtag('event', 'submit_email');
        globalThis.gtag('event', 'conversion', { send_to: 'AW-11237889467/XjBcCN2P1ugbELur0u4p' });
      }

      navigate(PATH_REGISTER.EMAIL);
    }
  }, [isSuccessValidateFields]);

  usePreventBack();

  const {
    handleSubmit,
    trigger,
    control,
    clearErrors,
    setError,
    formState: { isValid },
    watch,
  } = useForm({
    defaultValues: {
      email: emailFromUrl || '',
    },
    resolver: formResolver(
      object({
        email: emailScheme({ required: true }).label('Email'),
      }),
    ),
  });

  const email = watch('email');

  const registerByGoogle = async () => {
    const { APP_REDIRECT_URI, APP_GOOGLE_OAUTH_CLIENT_ID } = await getEnvironment();
    const scope = {
      profile: 'https://www.googleapis.com/auth/userinfo.profile',
      email: 'https://www.googleapis.com/auth/userinfo.email',
    };
    globalThis.location.href = `https://accounts.google.com/o/oauth2/auth?client_id=${APP_GOOGLE_OAUTH_CLIENT_ID}&redirect_uri=${encodeURIComponent(APP_REDIRECT_URI)}&scope=${encodeURIComponent([scope.profile, scope.email].join(' '))}&response_type=code&include_granted_scopes=true&&access_type=offline&state=state_parameter_passthrough_value&prompt=select_account+consent`;
  };

  const submit = async (data: { email: string }) => {
    if (isValid) {
      loader.show();

      try {
        const env = await getEnvironment();
        const isProduction = env.ENVIRONMENT === 'PROD';

        if (isProduction) {
          const isCompanyEmail = await fetch(`/api/is-company-email?email=${data.email}`).then(
            (res) => res.json(),
          );

          if (isCompanyEmail && !isCompanyEmail.result) {
            setError('email', {
              message: 'Please use your work email.',
            });
            setEmailErrorMessage('Please use your work email.');

            return loader.hide();
          }

          const mailgunRes = await fetch(`/api/mailgun-validate?email=${data.email}`).then((res) =>
            res.json(),
          );

          if (mailgunRes?.result && !mailgunRes.result.isValid) {
            setError('email', { message: mailgunRes?.result?.message || 'Invalid email' });
            setEmailErrorMessage(mailgunRes?.result?.message || '');

            return loader.hide();
          }
        }

        await validateFields({ body: { email: data.email } });
      } catch {
        loader.hide();
      }
    }
  };

  return (
    <AuthWrapper title={<Title />}>
      <div
        className={
          'flex flex-col w-[450px] md:w-[534px] rounded-[15px] border border-white bg-[#ffffff66] py-[25px] px-[46px]'
        }
      >
        <div>
          <Button
            onClick={registerByGoogle}
            className={
              'flex flex-row w-full gap-2.5 min-h-[50px] text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]'
            }
          >
            <img src={googleIcon} width="18px" alt="Google icon" />
            <span>Continue with Google</span>
          </Button>
        </div>
        <div>
          <div className="pt-7.5 pb-2.5 text-[#B8C1C6] flex flex-row justify-center overflow-hidden items-center relative">
            <div className={'h-px bg-[#B8C1C6] w-full'}></div>
            <p className="inline-block text-[#B8C1C6] text-sm font-medium text-center relative px-6 text-nowrap">
              OR
            </p>
            <div className={'h-px bg-[#B8C1C6] w-full'}></div>
          </div>
        </div>
        <form id={'register-form'} onSubmit={handleSubmit(submit)}>
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div className={'relative'}>
                <Input
                  type={'email'}
                  label={'Email'}
                  placeholder={'email@email.com'}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  onBlur={() => trigger('email')}
                  onFocus={() => {
                    clearErrors('email');
                    setEmailErrorMessage('');
                  }}
                />
                {Boolean(error?.message || emailErrorMessage) && (
                  <div
                    className={
                      'absolute top-[calc(100%+4px)] w-full bg-error_bg text-error text-center text-sm font-semibold font-jamjuree py-1 px-4 z-10 rounded-sm-5 shadow'
                    }
                  >
                    {error?.message || emailErrorMessage}
                  </div>
                )}
              </div>
            )}
          />
        </form>
        <div>
          <Button
            form={'register-form'}
            variant="green"
            className="min-h-[50px] w-full mt-10 text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]"
          >
            Continue with email
          </Button>
        </div>
        <div className={'mt-6'}>
          <p className={'w-full text-center text-xs text-ebony_opacity_50 font-medium'}>
            Already have an account?{' '}
            <Link className={'text-[#3F60B3] hover:underline'} to={PATH_AUTH_SPA.ROOT}>
              Log in
            </Link>
          </p>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default RegisterPage;
